import { useEffect, useMemo, useState } from 'react';
import { accounts, backend } from '../backend';
import { currentWorkspace } from '../workspace';
import { useApp } from '../store';
import { shortDay, humanDay } from '../when';
import { DEFAULT_WORKING_WEEK, type WorkCalendar, type Holiday } from '../work-calendar';
import DateField from './DateField';
import Collapsible from './Collapsible';
import Icon from './Icon';

/**
 * THE WORKSPACE WORKING CALENDAR — the working week and the holidays over it (0216),
 * tenant-wide and an admin's to set, read by the cadence forecast and the other
 * working-day signals. Shown in the two places currency is (the board Settings →
 * Workflow tab and the Workspace hub), the SAME editor and the SAME store state in
 * both. A non-admin sees the read-only summary; the server enforces the same guard
 * (the 0216 column grant + admin-of-own-workspace RLS), so the editor never offers a
 * write the database would refuse. Holidays import from a country's public set (the
 * `holidays` edge function) so they need not be typed one by one.
 */

/** The countries offered for import, grouped by region — the Caribbean, the Americas
 *  and the UK (LATAM/Caribbean/North America per the workspace's reach). Codes are
 *  ISO-3166 alpha-2, which the holiday source keys on. */
const COUNTRIES: { region: string; list: { code: string; name: string }[] }[] = [
  { region: 'Caribbean', list: [
    { code: 'TT', name: 'Trinidad & Tobago' }, { code: 'JM', name: 'Jamaica' }, { code: 'BB', name: 'Barbados' },
    { code: 'BS', name: 'Bahamas' }, { code: 'GD', name: 'Grenada' }, { code: 'LC', name: 'Saint Lucia' },
    { code: 'VC', name: 'St Vincent & the Grenadines' }, { code: 'DM', name: 'Dominica' },
    { code: 'AG', name: 'Antigua & Barbuda' }, { code: 'KN', name: 'St Kitts & Nevis' },
    { code: 'DO', name: 'Dominican Republic' }, { code: 'HT', name: 'Haiti' }, { code: 'CU', name: 'Cuba' },
    { code: 'PR', name: 'Puerto Rico' }, { code: 'KY', name: 'Cayman Islands' }, { code: 'BM', name: 'Bermuda' },
  ] },
  { region: 'North America', list: [
    { code: 'US', name: 'United States' }, { code: 'CA', name: 'Canada' }, { code: 'MX', name: 'Mexico' },
  ] },
  { region: 'Central & South America', list: [
    { code: 'GT', name: 'Guatemala' }, { code: 'BZ', name: 'Belize' }, { code: 'SV', name: 'El Salvador' },
    { code: 'HN', name: 'Honduras' }, { code: 'NI', name: 'Nicaragua' }, { code: 'CR', name: 'Costa Rica' },
    { code: 'PA', name: 'Panama' }, { code: 'CO', name: 'Colombia' }, { code: 'VE', name: 'Venezuela' },
    { code: 'GY', name: 'Guyana' }, { code: 'SR', name: 'Suriname' }, { code: 'EC', name: 'Ecuador' },
    { code: 'PE', name: 'Peru' }, { code: 'BR', name: 'Brazil' }, { code: 'BO', name: 'Bolivia' },
    { code: 'PY', name: 'Paraguay' }, { code: 'CL', name: 'Chile' }, { code: 'AR', name: 'Argentina' },
    { code: 'UY', name: 'Uruguay' },
  ] },
  { region: 'United Kingdom', list: [{ code: 'GB', name: 'United Kingdom' }] },
];

const DAY_LABELS: { dow: number; label: string }[] = [
  { dow: 1, label: 'Mon' }, { dow: 2, label: 'Tue' }, { dow: 3, label: 'Wed' }, { dow: 4, label: 'Thu' },
  { dow: 5, label: 'Fri' }, { dow: 6, label: 'Sat' }, { dow: 0, label: 'Sun' },
];
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** How the working week reads as a phrase: a contiguous run in Monday-first order
 *  collapses to "Monday to Friday"; anything else lists the days ("Monday, Wednesday,
 *  Friday"); a single day names itself. Empty is guarded against upstream. */
function weekLabel(working: Set<number>): string {
  const inOrder = DAY_LABELS.filter((d) => working.has(d.dow));
  if (inOrder.length === 0) return 'No working days';
  if (inOrder.length === 1) return DAY_NAMES[inOrder[0].dow];
  // The run is contiguous when every label between the first and last is working.
  const first = DAY_LABELS.findIndex((d) => working.has(d.dow));
  const last = DAY_LABELS.length - 1 - [...DAY_LABELS].reverse().findIndex((d) => working.has(d.dow));
  const contiguous = DAY_LABELS.slice(first, last + 1).every((d) => working.has(d.dow));
  if (contiguous) return `${DAY_NAMES[DAY_LABELS[first].dow]} to ${DAY_NAMES[DAY_LABELS[last].dow]}`;
  return inOrder.map((d) => DAY_NAMES[d.dow]).join(', ');
}

const MS_DAY = 86_400_000;
/** Inclusive calendar days from one YYYY-MM-DD to another (a one-week close reads 7). */
function spanDays(from: string, to: string): number {
  const at = (s: string) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, (m ?? 1) - 1, d ?? 1).getTime(); };
  return Math.round((at(to) - at(from)) / MS_DAY) + 1;
}

/** How a holiday's date reads in the list: an annual one drops the year ("1 Jan"); a
 *  one-off keeps it ("3 Apr 2026"); a range reads end-to-end with a calendar-day count
 *  ("24 Dec – 1 Jan 2027 · 9 days"), collapsing a same-year span to one month. */
function holidayDate(h: Holiday): string {
  if (h.until && h.until !== h.date) {
    const sameYear = h.date.slice(0, 4) === h.until.slice(0, 4);
    const from = h.annual ? shortDay(h.date) : sameYear ? shortDay(h.date) : humanDay(h.date);
    const to = h.annual ? shortDay(h.until) : humanDay(h.until);
    return `${from} – ${to} · ${spanDays(h.date, h.until)} days`;
  }
  return h.annual ? shortDay(h.date) : humanDay(h.date);
}

const holidayKey = (h: Holiday) => `${h.name}|${h.date}|${h.until ?? ''}|${h.annual ? 'A' : 'O'}`;
const byDate = (a: Holiday, b: Holiday) => (a.date < b.date ? -1 : a.date > b.date ? 1 : a.name.localeCompare(b.name));

export default function WorkCalendarEditor() {
  // Am I an admin of the workspace I am in? Read the way the hub does (workspaces()
  // runs as its owner and carries the flag), the same check CurrencyRates uses.
  const [isAdmin, setIsAdmin] = useState(false);
  useEffect(() => {
    let stop = false;
    accounts.workspaces()
      .then((list) => {
        if (stop) return;
        const here = currentWorkspace() || list[0]?.id || '';
        const mine = list.find((w) => w.id === here) ?? list[0];
        setIsAdmin(!!mine?.isAdmin);
      })
      .catch(() => setIsAdmin(false));
    return () => { stop = true; };
  }, []);

  const cal = useApp((s) => s.workCalendar);
  const setWorkCalendar = useApp((s) => s.setWorkCalendar);
  const [trouble, setTrouble] = useState('');

  const week = cal.week.length ? cal.week : DEFAULT_WORKING_WEEK;
  const workingSet = useMemo(() => new Set(week), [week]);
  const holidays = useMemo(() => [...cal.holidays].sort(byDate), [cal.holidays]);

  const save = (next: WorkCalendar) => {
    setTrouble('');
    void setWorkCalendar(next).catch(() =>
      setTrouble('That could not be saved. Only an administrator can change the working calendar.'),
    );
  };
  const toggleDay = (dow: number) => {
    const has = workingSet.has(dow);
    const nextWeek = has ? week.filter((d) => d !== dow) : [...week, dow].sort((a, b) => a - b);
    if (nextWeek.length === 0) { setTrouble('A workspace must work at least one day a week.'); return; }
    save({ ...cal, week: nextWeek });
  };
  const putHolidays = (list: Holiday[]) => {
    const seen = new Set<string>();
    const deduped = list.filter((h) => { const k = holidayKey(h); if (seen.has(k)) return false; seen.add(k); return true; });
    save({ ...cal, holidays: deduped });
  };

  // ── The add / edit a holiday form ───────────────────────────────────────────
  const [editing, setEditing] = useState<number | null | 'new'>(null); // index, 'new', or null (closed)
  const [fName, setFName] = useState('');
  const [fMode, setFMode] = useState<'single' | 'range'>('single');
  const [fDate, setFDate] = useState('');
  const [fUntil, setFUntil] = useState('');
  const [fAnnual, setFAnnual] = useState(false);
  const openEditor = (at: number | 'new') => {
    setTrouble('');
    if (at === 'new') { setFName(''); setFMode('single'); setFDate(''); setFUntil(''); setFAnnual(false); }
    else {
      const h = holidays[at];
      setFName(h.name); setFMode(h.until ? 'range' : 'single'); setFDate(h.date); setFUntil(h.until ?? ''); setFAnnual(!!h.annual);
    }
    setEditing(at);
  };
  const saveHoliday = () => {
    const name = fName.trim();
    if (!name) { setTrouble('Give the holiday a name.'); return; }
    if (!fDate) { setTrouble('Pick the date.'); return; }
    if (fMode === 'range' && (!fUntil || fUntil < fDate)) { setTrouble('The range must end on or after it starts.'); return; }
    const h: Holiday = { name, date: fDate, ...(fMode === 'range' && fUntil !== fDate ? { until: fUntil } : {}), ...(fAnnual ? { annual: true } : {}) };
    const next = editing === 'new' ? [...holidays, h] : holidays.map((old, i) => (i === editing ? h : old));
    putHolidays(next);
    setEditing(null);
  };
  const removeHoliday = (at: number) => { setTrouble(''); putHolidays(holidays.filter((_, i) => i !== at)); };

  // ── Import a country's public holidays ──────────────────────────────────────
  const [country, setCountry] = useState('');
  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState<Holiday[] | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [importTrouble, setImportTrouble] = useState('');
  const runImport = () => {
    if (!country || !backend.importHolidays) return;
    setImporting(true); setImportTrouble(''); setPreview(null);
    backend.importHolidays(country)
      .then((list) => {
        if (list.length === 0) { setImportTrouble('No public holidays were found for that country.'); return; }
        // Offer only the ones not already on the calendar.
        const have = new Set(holidays.map(holidayKey));
        const fresh = list.filter((h) => !have.has(holidayKey(h)));
        if (fresh.length === 0) { setImportTrouble('Every one of that country’s public holidays is already on your calendar.'); return; }
        setPreview(fresh);
        setPicked(new Set(fresh.map(holidayKey)));
      })
      .catch((e) => setImportTrouble(e instanceof Error ? e.message : 'The holidays could not be imported.'))
      .finally(() => setImporting(false));
  };
  const addImported = () => {
    if (!preview) return;
    const add = preview.filter((h) => picked.has(holidayKey(h)));
    putHolidays([...holidays, ...add]);
    setPreview(null); setPicked(new Set()); setCountry('');
  };

  if (!isAdmin) {
    return (
      <p className="hint">
        The working week is {weekLabel(workingSet)}
        {holidays.length > 0 ? `, with ${holidays.length} holiday${holidays.length === 1 ? '' : 's'} set` : ', with no holidays set'} for the whole
        workspace by an administrator.
      </p>
    );
  }

  return (
    <div className="hol">
      <div className="hol-scope">
        <Icon name="globe" size={15} />
        <span>One calendar for the whole workspace · set by an admin</span>
      </div>

      <div className="hol-sec">
        <div className="fd-sec-h">Working Week</div>
        <p className="hol-week-summary">
          <b>{weekLabel(workingSet)}</b> · {week.length} working day{week.length === 1 ? '' : 's'} a week. Tap a day to
          include or exclude it — a team that works Sunday to Thursday sets it here.
        </p>
        <div className="hol-week" role="group" aria-label="Working week">
          {DAY_LABELS.map(({ dow, label }) => {
            const on = workingSet.has(dow);
            return (
              <button
                key={dow}
                type="button"
                className={`hol-day ${on ? 'on' : ''}`}
                aria-pressed={on}
                aria-label={`${DAY_NAMES[dow]} — ${on ? 'working day' : 'not a working day'}`}
                onClick={() => toggleDay(dow)}
              >
                <span className="hol-day-tick" aria-hidden="true">{on && <Icon name="check" size={11} />}</span>
                <span className="hol-day-name">{label}</span>
                <span className="hol-day-state">{on ? 'Working' : 'Off'}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="hol-sec">
        <div className="fd-sec-h">Holidays</div>
        <p className="hint">Days the whole workspace is closed. Import a country’s public holidays, then add any of your own.</p>

        {/* Import a country's public holidays */}
        <div className="hol-import">
          <select className="input hol-import-pick" value={country} onChange={(e) => { setCountry(e.target.value); setPreview(null); setImportTrouble(''); }} aria-label="Country to import holidays from">
            <option value="">Import a country’s public holidays…</option>
            {COUNTRIES.map((grp) => (
              <optgroup key={grp.region} label={grp.region}>
                {grp.list.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
              </optgroup>
            ))}
          </select>
          <button className="btn btn-small" disabled={!country || importing} onClick={runImport}>
            {importing ? 'Importing…' : 'Import'}
          </button>
        </div>
        {importTrouble && <p className="hint hol-import-note" role="alert">{importTrouble}</p>}
        <Collapsible show={!!preview} className="hol-preview">
          {preview && (
            <div className="hol-preview-in">
              <div className="hol-preview-h">{preview.length} holiday{preview.length === 1 ? '' : 's'} to add — untick any you don’t observe.</div>
              <div className="hol-preview-list">
                {preview.map((h) => {
                  const k = holidayKey(h);
                  const on = picked.has(k);
                  return (
                    <label key={k} className="hol-preview-row">
                      <input type="checkbox" checked={on} onChange={(e) => setPicked((p) => { const n = new Set(p); if (e.target.checked) n.add(k); else n.delete(k); return n; })} />
                      <span className="hol-preview-name">{h.name}</span>
                      <span className="hol-preview-date">{holidayDate(h)}</span>
                    </label>
                  );
                })}
              </div>
              <div className="hol-preview-foot">
                <button className="btn btn-small btn-primary" disabled={picked.size === 0} onClick={addImported}>Add {picked.size} holiday{picked.size === 1 ? '' : 's'}</button>
                <button className="btn btn-small" onClick={() => { setPreview(null); setPicked(new Set()); }}>Cancel</button>
              </div>
            </div>
          )}
        </Collapsible>

        {/* The holiday list */}
        {holidays.length === 0 ? (
          <div className="hol-empty">
            <div className="hol-empty-title">No holidays added yet</div>
            <p className="hol-empty-sub">A working-day forecast still skips weekends. Add the days your workspace is closed so a known close pushes a prediction out.</p>
          </div>
        ) : (
          <table className="hol-table">
            <thead>
              <tr><th>Name</th><th>Date</th><th>Recurs</th><th aria-label="Actions" /></tr>
            </thead>
            <tbody>
              {holidays.map((h, i) => (
                <tr key={holidayKey(h)}>
                  <td className="hol-name">{h.name}</td>
                  <td className="hol-date">{holidayDate(h)}</td>
                  <td className="hol-recurs">
                    {h.annual
                      ? <span className="hol-annual"><span className="hol-annual-mark" aria-hidden="true" />Every year</span>
                      : <span className="hol-oneoff">This year only</span>}
                  </td>
                  <td className="hol-row-actions">
                    <button className="btn btn-small" onClick={() => openEditor(i)}>Edit</button>
                    <button className="btn btn-small hol-remove" onClick={() => removeHoliday(i)}>Remove</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {editing === null && (
          <button className="btn hol-add" onClick={() => openEditor('new')}>Add a holiday</button>
        )}
        <Collapsible show={editing !== null} className="hol-editor">
          {editing !== null && (
            <div className="hol-editor-in">
              <div className="hol-editor-h">{editing === 'new' ? 'Add a holiday' : 'Edit holiday'}</div>
              <label className="stack">
                <span>Name <span className="req" aria-hidden="true">*</span></span>
                <input className="input" value={fName} onChange={(e) => setFName(e.target.value)} placeholder="e.g. Independence Day" />
              </label>
              <div className="seg hol-editor-mode" role="tablist" aria-label="How long">
                {(['single', 'range'] as const).map((m) => (
                  <button key={m} type="button" role="tab" aria-selected={fMode === m} className={`seg-btn ${fMode === m ? 'active' : ''}`} onClick={() => setFMode(m)}>
                    {m === 'single' ? 'Single day' : 'Date range'}
                  </button>
                ))}
              </div>
              <div className="hol-editor-dates">
                <label className="stack">
                  <span>{fMode === 'range' ? 'From' : 'Date'} <span className="req" aria-hidden="true">*</span></span>
                  <DateField value={fDate} onChange={setFDate} ariaLabel="Holiday date" ariaRequired variant="form" />
                </label>
                {fMode === 'range' && (
                  <label className="stack">
                    <span>To <span className="req" aria-hidden="true">*</span></span>
                    <DateField value={fUntil} onChange={setFUntil} ariaLabel="Holiday end date" ariaRequired variant="form" min={fDate} />
                  </label>
                )}
              </div>
              <div className={`feature-row hol-editor-annual ${fAnnual ? 'is-on' : ''}`}>
                <button className={`switch ${fAnnual ? 'on' : ''}`} role="switch" aria-checked={fAnnual} aria-label="Repeats every year" onClick={() => setFAnnual((v) => !v)}>
                  <span className="switch-knob" />
                </button>
                <div className="feature-main">
                  <div className="hol-editor-annual-title">Repeats every year</div>
                  <div className="hol-editor-annual-sub">On for a fixed date like New Year’s Day; off for a one-off such as a 2026 office move.</div>
                </div>
              </div>
              <div className="hol-editor-foot">
                <button className="btn btn-primary" onClick={saveHoliday}>{editing === 'new' ? 'Add holiday' : 'Save'}</button>
                <button className="btn" onClick={() => { setEditing(null); setTrouble(''); }}>Cancel</button>
              </div>
            </div>
          )}
        </Collapsible>
      </div>

      {trouble && <p className="acc-trouble" role="alert">{trouble}</p>}
    </div>
  );
}
